import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";

import FilterListItem from "@client/components/modules/FilterList/Item";
import FilterListWrapper from "@client/components/modules/FilterList/Wrapper";
import ListTemplate from "@client/components/templates/ListTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import licensorImage from "@client/components/ui/sharedImages/licensor.svg";
import licenceStore from "@client/stores/LicenceStore";
import licensorStore from "@client/stores/LicensorStore";
import roleStore from "@client/stores/RoleStore";
import Style from "@client/utils/Style";
import { RoleAssignment } from "@typings/Identity";
import { Licensor } from "@typings/Licensor";

const Wrapper = styled.div`
  height: 100%;
`;

type State = {
  selectedLicensors: Licensor[];
  showDeleteAlert: boolean;
  showDeleteErrorAlert: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
};

@observer
class LicensorsPage extends React.Component<Props, State> {
  state: State = {
    selectedLicensors: [],
    showDeleteAlert: false,
    showDeleteErrorAlert: false,
  };

  componentDidMount() {
    licensorStore.load();
    roleStore.load();
    roleStore.loadRoleAssignments();
    licenceStore.load();
  }

  handleItemClick(licensor: Licensor) {
    this.props.onNavigate(`/licensors/${licensor.id}`);
  }

  handleReloadButtonClick() {
    licensorStore.load({ forceShowLoading: true });
    roleStore.load();
    roleStore.loadRoleAssignments();
    licenceStore.load();
  }

  itemFilterFunction(
    licensor: Licensor,
    filterItem?: string | null,
    filterText?: string,
  ) {
    filterText = (filterText || "").toLowerCase();
    filterItem = filterItem || "";
    const roleAssignments: RoleAssignment[] = roleStore.roleAssignments.filter(
      ra =>
        ra.user.id === licensor.identity_id &&
        ra.scope &&
        ra.scope.project &&
        licensorStore.tokenInfo &&
        ra.scope.project.id === licensorStore.tokenInfo.project.id,
    );

    const searchGroup =
      filterItem === "all" ||
      roleAssignments.find(ra => ra.role.name === filterItem);

    const searchText =
      licensor.id.toString().indexOf(filterText) > -1 ||
      licensor.name.toLowerCase().indexOf(filterText) > -1 ||
      (licensor.description &&
        licensor.description.toLowerCase().indexOf(filterText) > -1) ||
      (licensor.email && licensor.email.toLowerCase().indexOf(filterText) > -1);

    if (searchGroup && searchText) {
      return true;
    }

    return false;
  }

  getListColumns(licensor: Licensor) {
    const userRoleAssignments = roleStore.roleAssignments.filter(
      ra => ra.user.id === licensor.identity_id,
    );
    const role =
      userRoleAssignments.find(ra =>
        roleStore.roles.find(r => r === ra.role.name),
      )?.role.name || "-";
    const licences = licenceStore.licences.filter(
      l => l.created_by === licensor.name,
    );

    return [
      {
        name: "Email",
        value: licensor.email || "-",
        style: { minWidth: "204px", maxWidth: "204px" },
      },
      {
        name: "Role",
        value: role,
        style: { minWidth: "94px", maxWidth: "94px" },
      },
      {
        name: "Licences generated",
        value: `${licences.length}`,
        style: { minWidth: "144px", maxWidth: "144px" },
      },
    ];
  }

  handleDeleteBulkAction() {
    this.setState({ showDeleteAlert: true });
  }

  async handleDeleteBulkLicensors() {
    this.setState({ showDeleteAlert: false });
    this.state.selectedLicensors.forEach(licensor => {
      licensorStore.delete(licensor.id);
    });
  }

  render() {
    const BulkActions: DropdownActionType[] = [
      {
        label: "Delete Licence Managers",
        color: Style.palette.alert,
        action: () => {
          this.handleDeleteBulkAction();
        },
      },
    ];
    const licensors = licensorStore.licensors;

    return (
      <Wrapper>
        <ListTemplate
          title="Coriolis Licence Managers"
          listComponent={
            <FilterListWrapper
              filterItems={[
                { label: "All", value: "all" },
                ...roleStore.roles.map(r => ({
                  label: r,
                  value: r,
                })),
              ]}
              dropdownActions={BulkActions}
              onSelectedItemsChange={selectedLicensors => {
                this.setState({ selectedLicensors: selectedLicensors });
              }}
              selectionLabel="licence managers"
              loading={licensorStore.loading}
              items={licensors}
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              itemFilterFunction={(...args) => this.itemFilterFunction(...args)}
              renderItemComponent={options => {
                const licensor: Licensor = options.item;
                return (
                  <FilterListItem
                    {...options}
                    image={licensorImage}
                    title={licensor.name}
                    subtitle={licensor.description}
                    columns={this.getListColumns(licensor)}
                  />
                );
              }}
            />
          }
          footerText={`${licensors.length} licence managers`}
        />
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Licence Managers?"
            message="Are you sure you want to delete the selected licence managers?"
            extraMessage="Deleting a Coriolis Licence Manager is permanent!"
            onConfirmation={() => {
              this.handleDeleteBulkLicensors();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showDeleteErrorAlert ? (
          <AlertModal
            isOpen
            type="error"
            title="Cannot Delete Licence Managers"
            message="Cannot delete the last licence manager with the admin role"
            extraMessage="You must first assign another licence manager to the admin role"
            onRequestClose={() => {
              this.setState({ showDeleteErrorAlert: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function LicensorsPageWithNavigate() {
  const navigate = useNavigate();
  return <LicensorsPage onNavigate={navigate} />;
}

export default LicensorsPageWithNavigate;
